/**
 * Dropdown Data API Hooks
 * React Query hooks for fetching dropdown options
 */

import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import AxiosClient from '@/api/client';
import { Endpoints, EndpointHelpers } from '@/config/endpoints';
import type {
  DropdownRole,
  DropdownProperty,
  DropdownPropertyType,
  DropdownDepartment,
  DropdownTouchpoint,
} from '@/types/api';

// ============================================================================
// Query Keys
// ============================================================================

export const dropdownQueryKeys = {
  all: ['dropdowns'] as const,
  roles: () => [...dropdownQueryKeys.all, 'roles'] as const,
  properties: () => [...dropdownQueryKeys.all, 'properties'] as const,
  propertyTypes: () => [...dropdownQueryKeys.all, 'property-types'] as const,
  departments: () => [...dropdownQueryKeys.all, 'departments'] as const,
  departmentsByPropertyType: (propertyTypeId: string | number) =>
    [
      ...dropdownQueryKeys.all,
      'departments-by-property-type',
      propertyTypeId,
    ] as const,
  touchpointsByDepartment: (departmentId: string | number) =>
    [
      ...dropdownQueryKeys.all,
      'touchpoints-by-department',
      departmentId,
    ] as const,
  touchpointsByPropertyType: (propertyTypeId: string | number) =>
    [
      ...dropdownQueryKeys.all,
      'touchpoints-by-property-type',
      propertyTypeId,
    ] as const,
  titlesByPropertyType: (propertyTypeId: string | number) =>
    [
      ...dropdownQueryKeys.all,
      'titles-by-property-type',
      propertyTypeId,
    ] as const,
  subTouchpointsByPropertyType: (propertyTypeId: string | number) =>
    [
      ...dropdownQueryKeys.all,
      'sub-touchpoints-by-property-type',
      propertyTypeId,
    ] as const,
} as const;

// ============================================================================
// Roles Dropdown Query
// ============================================================================

export function useRolesDropdownQuery() {
  return useQuery({
    queryKey: dropdownQueryKeys.roles(),
    queryFn: async (): Promise<DropdownRole[]> => {
      const client = new AxiosClient<undefined, DropdownRole>({
        endpoint: Endpoints.DROPDOWN_ROLES,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getList();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch roles');
      }

      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (roles rarely change)
    retry: (failureCount, error) => {
      // Retry on network errors but not on auth errors
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Properties Dropdown Query
// ============================================================================

export function usePropertiesDropdownQuery() {
  return useQuery({
    queryKey: dropdownQueryKeys.properties(),
    queryFn: async (): Promise<DropdownProperty[]> => {
      const client = new AxiosClient<undefined, DropdownProperty>({
        endpoint: Endpoints.DROPDOWN_PROPERTIES,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getList();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch properties');
      }

      return response.data;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Property Types Dropdown Query
// ============================================================================

export function usePropertyTypesDropdownQuery() {
  return useQuery({
    queryKey: dropdownQueryKeys.propertyTypes(),
    queryFn: async (): Promise<DropdownPropertyType[]> => {
      const client = new AxiosClient<undefined, DropdownPropertyType>({
        endpoint: Endpoints.DROPDOWN_PROPERTY_TYPES,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getList();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch property types');
      }

      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (property types rarely change)
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Departments Dropdown Query
// ============================================================================

export function useDepartmentsDropdownQuery() {
  return useQuery({
    queryKey: dropdownQueryKeys.departments(),
    queryFn: async (): Promise<DropdownDepartment[]> => {
      const client = new AxiosClient<undefined, DropdownDepartment>({
        endpoint: Endpoints.DROPDOWN_DEPARTMENTS,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getList();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch departments');
      }

      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (departments rarely change)
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// SRS Hierarchy Dropdown Queries
// ============================================================================

export function useDepartmentsByPropertyTypeQuery(
  propertyTypeId: string | number | null
) {
  return useQuery({
    queryKey: dropdownQueryKeys.departmentsByPropertyType(propertyTypeId || 0),
    queryFn: async (): Promise<DropdownDepartment[]> => {
      if (!propertyTypeId) {
        return [];
      }

      const client = new AxiosClient<undefined, DropdownDepartment>({
        endpoint: EndpointHelpers.getDepartmentsByPropertyType(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getList();

      if (!response.status) {
        throw new Error(
          response.error || 'Failed to fetch departments by property type'
        );
      }

      return response.data;
    },
    enabled: !!propertyTypeId,
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useTouchpointsByDepartmentQuery(
  departmentId: string | number | null
) {
  return useQuery({
    queryKey: dropdownQueryKeys.touchpointsByDepartment(departmentId || 0),
    queryFn: async (): Promise<DropdownTouchpoint[]> => {
      if (!departmentId) {
        return [];
      }

      const client = new AxiosClient<undefined, DropdownTouchpoint>({
        endpoint: EndpointHelpers.getTouchpointsByDepartment(departmentId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getList();

      if (!response.status) {
        throw new Error(
          response.error || 'Failed to fetch touchpoints by department'
        );
      }

      return response.data;
    },
    enabled: !!departmentId,
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useTouchpointsByPropertyTypeQuery(
  propertyTypeId: string | number | null
) {
  return useQuery({
    queryKey: dropdownQueryKeys.touchpointsByPropertyType(propertyTypeId || 0),
    queryFn: async (): Promise<DropdownTouchpoint[]> => {
      if (!propertyTypeId) {
        return [];
      }

      const client = new AxiosClient<undefined, DropdownTouchpoint>({
        endpoint: EndpointHelpers.getTouchpointsByPropertyType(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getList();

      if (!response.status) {
        throw new Error(
          response.error || 'Failed to fetch touchpoints by property type'
        );
      }

      return response.data;
    },
    enabled: !!propertyTypeId,
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Get role options formatted for Select components
 */
export function useRoleOptions() {
  const { data, isLoading } = useRolesDropdownQuery();

  return {
    options: (Array.isArray(data) ? data : []).map(role => ({
      value: role.name,
      label: role.label,
    })),
    isLoading,
  };
}

/**
 * Get property options formatted for Select components
 */
export function usePropertyOptions() {
  const { data, isLoading } = usePropertiesDropdownQuery();

  return {
    options: (Array.isArray(data) ? data : []).map(property => ({
      value: property.id.toString(),
      label: property.label,
    })),
    isLoading,
  };
}

/**
 * Get department options formatted for Select components
 */
export function useDepartmentOptions() {
  const { data, isLoading } = useDepartmentsDropdownQuery();

  return {
    options: (Array.isArray(data) ? data : []).map(department => ({
      value: department.id.toString(),
      label: department.label,
    })),
    isLoading,
  };
}

/**
 * Get property type options formatted for Select components
 */
export function usePropertyTypeOptions() {
  const { data, isLoading } = usePropertyTypesDropdownQuery();

  return {
    options: (Array.isArray(data) ? data : []).map(propertyType => ({
      value: propertyType.id.toString(),
      label: propertyType.label,
    })),
    isLoading,
  };
}

/**
 * Get department options by property type formatted for Select components
 */
export function useDepartmentsByPropertyTypeOptions(
  propertyTypeId: string | number | null
) {
  const { data, isLoading } = useDepartmentsByPropertyTypeQuery(propertyTypeId);

  return {
    options: (Array.isArray(data) ? data : []).map(department => ({
      value: department.id.toString(),
      label: department.label,
    })),
    isLoading,
  };
}

/**
 * Get touchpoint options by property type formatted for Select components
 */
export function useTouchpointsByPropertyTypeOptions(
  propertyTypeId: string | number | null
) {
  const { data, isLoading } = useTouchpointsByPropertyTypeQuery(propertyTypeId);

  return {
    options: (Array.isArray(data) ? data : []).map(touchpoint => ({
      value: touchpoint.id.toString(),
      label: touchpoint.label,
    })),
    isLoading,
  };
}
